'use client'

import { RecoveryFlow, UpdateRecoveryFlowBody } from '@ory/kratos-client'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import kratos from '@/utils/ory-client'

export type PasswordResetStep = 'email' | 'code' | 'success'

export interface PasswordResetFormData {
  email: string
  code: string
}

interface PasswordResetAlerts {
  errorMessage: string | null
  successMessage: string | null
}

export function usePasswordResetFlow() {
  const [currentStep, setCurrentStep] = useState<PasswordResetStep>('email')
  const [isLoading, setIsLoading] = useState(false)
  const [isInitializing, setIsInitializing] = useState(true)
  const [email, setEmail] = useState('')
  const [alerts, setAlerts] = useState<PasswordResetAlerts>({ errorMessage: null, successMessage: null })
  const [recoveryFlow, setRecoveryFlow] = useState<RecoveryFlow | null>(null)

  const form = useForm<PasswordResetFormData>({
    defaultValues: {
      email: '',
      code: ''
    }
  })

  const getCsrfToken = useCallback(() => {
    if (!recoveryFlow) return ''

    const csrfNode = recoveryFlow.ui.nodes.find(
      (node: any) => node.attributes?.name === 'csrf_token' && node.attributes?.node_type === 'input'
    )

    const attributes = csrfNode?.attributes as { value?: string } | undefined
    const csrfToken = attributes?.value
    return typeof csrfToken === 'string' ? csrfToken : ''
  }, [recoveryFlow])

  const initializeRecoveryFlow = useCallback(async () => {
    if (typeof window === 'undefined') return

    try {
      setIsInitializing(true)
      const flow = await kratos.createBrowserRecoveryFlow(
        {
          returnTo: window.location.origin
        },
        {
          withCredentials: true,
          headers: { Accept: 'application/json' }
        }
      )
      setRecoveryFlow(flow.data)
    } catch (error) {
      setAlerts({
        errorMessage: 'Failed to initialize password reset. Please refresh the page.',
        successMessage: null
      })
    } finally {
      setIsInitializing(false)
    }
  }, [])

  useEffect(() => {
    initializeRecoveryFlow()
  }, [initializeRecoveryFlow])

  const updateAlerts = useCallback((updates: Partial<PasswordResetAlerts>) => {
    setAlerts((previous) => ({ ...previous, ...updates }))
  }, [])

  const handleSendCode = useCallback(
    async (data: PasswordResetFormData) => {
      if (isInitializing || !recoveryFlow) {
        updateAlerts({ errorMessage: 'Please wait for the page to initialize...' })
        return
      }

      setIsLoading(true)
      updateAlerts({ errorMessage: null, successMessage: null })

      try {
        if (!recoveryFlow.id) {
          throw new Error('Recovery flow not found. Please refresh the page.')
        }

        const updateBody: UpdateRecoveryFlowBody = {
          method: 'code',
          email: data.email,
          csrf_token: getCsrfToken()
        }

        const response = await kratos.updateRecoveryFlow(
          { flow: recoveryFlow.id, updateRecoveryFlowBody: updateBody },
          {
            withCredentials: true,
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
          }
        )

        const responseData = response.data

        const errorMessage = responseData?.ui?.messages?.find((message: any) => message.type === 'error')?.text
        if (errorMessage) {
          throw new Error(errorMessage)
        }

        if (responseData?.state === 'sent_email') {
          setEmail(data.email)
          setCurrentStep('code')
          updateAlerts({ successMessage: 'Reset code sent to your email!' })
        } else {
          updateAlerts({ errorMessage: 'Unexpected response from server. Please try again.' })
        }
      } catch (error: any) {
        const uiError = error.response?.data?.ui?.messages?.find((message: any) => message.type === 'error')?.text
        const apiError = error.response?.data?.error?.message
        const statusError = error.response?.status
          ? `Server error (${error.response.status}): ${error.response.statusText || 'Unknown error'}`
          : null

        updateAlerts({
          errorMessage:
            uiError || apiError || statusError || error.message || 'Failed to send reset code. Please try again.'
        })
      } finally {
        setIsLoading(false)
      }
    },
    [getCsrfToken, isInitializing, recoveryFlow, updateAlerts]
  )

  const handleVerifyCode = useCallback(
    async (data: PasswordResetFormData) => {
      setIsLoading(true)
      updateAlerts({ errorMessage: null, successMessage: null })

      try {
        if (!recoveryFlow?.id) {
          throw new Error('Recovery flow not found. Please refresh the page.')
        }

        const updateBody: UpdateRecoveryFlowBody = {
          method: 'code',
          code: data.code,
          csrf_token: getCsrfToken()
        }

        const response = await kratos.updateRecoveryFlow(
          { flow: recoveryFlow.id, updateRecoveryFlowBody: updateBody },
          {
            withCredentials: true,
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
          }
        )

        const responseData = response.data
        const errorMessage = responseData?.ui?.messages?.find((message: any) => message.type === 'error')?.text
        if (errorMessage) {
          throw new Error(errorMessage)
        }

        if (responseData?.state === 'passed_challenge') {
          updateAlerts({ successMessage: 'Code verified successfully! Redirecting to your account...' })
          if (typeof window !== 'undefined') {
            window.location.href = '/settings?from=recovery_code'
          }
        } else if (responseData?.state === 'sent_email') {
          throw new Error('Code verification failed. Please check your code and try again.')
        } else {
          updateAlerts({ errorMessage: 'Unexpected response from server. Please try again.' })
        }
      } catch (error: any) {
        const uiError = error.response?.data?.ui?.messages?.find((message: any) => message.type === 'error')?.text
        const apiError = error.response?.data?.error?.message
        const statusError = error.response?.status
          ? `Server error (${error.response.status}): ${error.response.statusText || 'Unknown error'}`
          : null

        updateAlerts({
          errorMessage: uiError || apiError || statusError || error.message || 'Failed to verify code. Please try again.'
        })
      } finally {
        setIsLoading(false)
      }
    },
    [getCsrfToken, recoveryFlow, updateAlerts]
  )

  const handleResendCode = useCallback(async () => {
    if (isInitializing || !recoveryFlow) {
      updateAlerts({ errorMessage: 'Please wait for the page to initialize...' })
      return
    }

    setIsLoading(true)
    updateAlerts({ errorMessage: null, successMessage: null })

    try {
      if (!recoveryFlow?.id) {
        throw new Error('Recovery flow not found. Please refresh the page.')
      }

      const updateBody: UpdateRecoveryFlowBody = {
        method: 'code',
        email,
        csrf_token: getCsrfToken()
      }

      const response = await kratos.updateRecoveryFlow(
        { flow: recoveryFlow.id, updateRecoveryFlowBody: updateBody },
        {
          withCredentials: true,
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
        }
      )

      const responseData = response.data
      const errorMessage = responseData?.ui?.messages?.find((message: any) => message.type === 'error')?.text
      if (errorMessage) {
        throw new Error(errorMessage)
      }

      if (responseData?.state === 'sent_email') {
        updateAlerts({ successMessage: 'Code resent successfully!' })
      } else {
        updateAlerts({ errorMessage: 'Unexpected response from server. Please try again.' })
      }
    } catch (error: any) {
      const uiError = error.response?.data?.ui?.messages?.find((message: any) => message.type === 'error')?.text
      const apiError = error.response?.data?.error?.message
      const statusError = error.response?.status
        ? `Server error (${error.response.status}): ${error.response.statusText || 'Unknown error'}`
        : null

      updateAlerts({
        errorMessage:
          uiError || apiError || statusError || error.message || 'Failed to resend code. Please try again.'
      })
    } finally {
      setIsLoading(false)
    }
  }, [email, getCsrfToken, isInitializing, recoveryFlow, updateAlerts])

  const resetToEmailStep = useCallback(() => {
    setCurrentStep('email')
    form.reset({ email, code: '' })
    updateAlerts({ errorMessage: null, successMessage: null })
  }, [email, form, updateAlerts])

  const stepMetadata = useMemo(() => {
    switch (currentStep) {
      case 'email':
        return {
          title: 'Reset password',
          description: 'Get a verification code'
        }
      case 'code':
        return {
          title: 'Enter verification code',
          description: 'Enter the verification code'
        }
      case 'success':
        return {
          title: 'Success!',
          description: ''
        }
      default:
        return {
          title: 'Reset password',
          description: ''
        }
    }
  }, [currentStep])

  return {
    form,
    currentStep,
    isLoading,
    isInitializing,
    email,
    ...alerts,
    handleSendCode,
    handleVerifyCode,
    handleResendCode,
    resetToEmailStep,
    setCurrentStep,
    stepMetadata
  }
}
